import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import api from './api';

/**
 * Check if running inside Capacitor native app
 */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Initialize all Capacitor plugins on app start
 */
export async function initCapacitor(): Promise<void> {
  if (!isNativeApp()) return;

  await initStatusBar();
  await initDeepLinks();
  await initOrientationLock();
  await hideSplash();
}

/**
 * Lock the app to portrait on startup. iOS Info.plist allows landscape
 * and Android's manifest is `unspecified` (as of the 1.0.6 rebuild) so
 * the OS will happily rotate the whole app if we don't clamp it here.
 * QuizTaking unlocks temporarily while a quiz video is in fullscreen
 * and re-locks on exit — every other page stays portrait as a result.
 */
async function initOrientationLock(): Promise<void> {
  try {
    const { ScreenOrientation } = await import('@capacitor/screen-orientation');
    await ScreenOrientation.lock({ orientation: 'portrait' });
  } catch (e) {
    console.warn('ScreenOrientation plugin not available', e);
  }
}

/**
 * Configure status bar for dark theme
 */
async function initStatusBar(): Promise<void> {
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#0f172a' });
  } catch (e) {
    console.warn('StatusBar plugin not available', e);
  }
}

/**
 * Hide splash screen after app is ready
 */
async function hideSplash(): Promise<void> {
  try {
    await SplashScreen.hide({ fadeOutDuration: 500 });
  } catch (e) {
    console.warn('SplashScreen plugin not available', e);
  }
}

/**
 * Initialize deep link handling
 */
async function initDeepLinks(): Promise<void> {
  try {
    CapApp.addListener('appUrlOpen', (event) => {
      const url = new URL(event.url);
      const path = url.pathname;
      if (path) {
        window.location.hash = '';
        window.history.replaceState(null, '', path + url.search);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    });
  } catch (e) {
    console.warn('Deep link listener not available', e);
  }
}

/**
 * Register for push notifications and send token to backend
 * Call this AFTER the user is authenticated
 */
export async function registerPushNotifications(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    // Request permission
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('Push notification permission not granted');
      return;
    }

    // Register with APNs/FCM
    await PushNotifications.register();

    // Listen for registration token. We detect the runtime platform
    // via Capacitor.getPlatform() instead of hardcoding "android" so
    // iOS devices land in the DB with the right tag — the existing
    // 37 entries are all Android because no iPhone has registered
    // yet, but as soon as one does we need iOS routing to work.
    PushNotifications.addListener('registration', async (token) => {
      const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
      console.log('Push registration token (', platform, '):', token.value);
      try {
        await api.post('/notifications/devices', {
          token: token.value,
          platform,
        });
      } catch (e) {
        console.error('Failed to register device token with backend', e);
      }
    });

    // Handle registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    // Handle received notifications when app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      // Dispatch custom event so the UI can refresh notification badge/list
      window.dispatchEvent(new CustomEvent('push-received', { detail: notification }));
    });

    // Handle notification tap (app opened from notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification.data;
      if (data?.url) {
        window.history.replaceState(null, '', data.url);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    });
  } catch (e) {
    console.error('Failed to initialize push notifications', e);
  }
}

/**
 * Unregister push notifications (call on logout)
 */
export async function unregisterPushNotifications(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    await PushNotifications.removeAllListeners();
  } catch (e) {
    console.warn('Failed to unregister push notifications', e);
  }
}

export default api;
